"use client";

import { c as _c } from "react/compiler-runtime";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import { Spinner } from "@/components/dotLoader/DotLoader";
import CommonIntersectionElement from "@/components/utils/CommonIntersectionElement";
import Drop, { DropLocation } from "@/components/waves/drops/Drop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropCurationMembershipMutation } from "@/hooks/drops/useDropCurationMembershipMutation";
import { useDropCurations } from "@/hooks/drops/useDropCurations";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import { useWaveDrops } from "@/hooks/useWaveDrops";
import type { ApiWave } from "@/generated/models/ApiWave";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo, type ReactNode } from "react";
import { useLayout } from "../layout/LayoutContext";
interface MyStreamWaveCurationContentProps {
  readonly wave: ApiWave;
  readonly curationId: string;
  readonly curationName?: string | null | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
function MyStreamWaveCurationDropItem(t0) {
  const $ = _c(25);
  const {
    drop,
    previousDrop,
    nextDrop,
    curationId,
    canManageActiveCuration,
    onDropClick
  } = t0;
  const {
    hasTouchScreen,
    isApp
  } = useDeviceInfo();
  const isTouchDevice = useIsTouchDevice();
  let t1;
  if ($[0] !== drop.id) {
    t1 = {
      dropId: drop.id
    };
    $[0] = drop.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    updateMembership,
    isPending
  } = useDropCurationMembershipMutation(t1);
  const shouldUseDetachedRemoveButton = isTouchDevice || isApp && hasTouchScreen;
  let t2;
  if ($[2] !== curationId || $[3] !== updateMembership) {
    t2 = () => {
      updateMembership(curationId, "remove");
    };
    $[2] = curationId;
    $[3] = updateMembership;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleRemove = t2;
  const t3 = `tw-group tw-relative ${canManageActiveCuration && shouldUseDetachedRemoveButton ? "tw-pt-5" : ""}`;
  let t4;
  if ($[5] !== canManageActiveCuration || $[6] !== handleRemove || $[7] !== isPending || $[8] !== shouldUseDetachedRemoveButton) {
    t4 = canManageActiveCuration && shouldUseDetachedRemoveButton && <button type="button" onClick={event => {
      event.stopPropagation();
      handleRemove();
    }} disabled={isPending} aria-label="Remove drop from this curation" className="tw-absolute tw-right-5 tw-top-0 tw-z-20 tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-300 tw-transition-colors tw-duration-200 active:tw-text-iron-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-60">{isPending ? <Spinner dimension={12} /> : <XMarkIcon className="tw-size-4 tw-flex-shrink-0" />}</button>;
    $[5] = canManageActiveCuration;
    $[6] = handleRemove;
    $[7] = isPending;
    $[8] = shouldUseDetachedRemoveButton;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== drop || $[11] !== nextDrop || $[12] !== onDropClick || $[13] !== previousDrop) {
    t5 = <Drop key={drop.stableKey} drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} dropViewDropId={null} onReply={_temp} onReplyClick={_temp2} onQuoteClick={_temp3} onDropContentClick={onDropClick} />;
    $[10] = drop;
    $[11] = nextDrop;
    $[12] = onDropClick;
    $[13] = previousDrop;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== canManageActiveCuration || $[16] !== handleRemove || $[17] !== isPending || $[18] !== shouldUseDetachedRemoveButton) {
    t6 = canManageActiveCuration && !shouldUseDetachedRemoveButton && <button type="button" onClick={event_0 => {
      event_0.stopPropagation();
      handleRemove();
    }} disabled={isPending} aria-label="Remove drop from this curation" className="tw-absolute tw-right-7 tw-top-4 tw-z-20 tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-rose-500/25 tw-bg-rose-500/10 tw-p-0 tw-text-rose-400 tw-shadow-[0_10px_30px_rgba(0,0,0,0.32)] tw-backdrop-blur-sm tw-transition-all tw-duration-200 tw-ease-out active:tw-bg-rose-500/15 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:tw-pointer-events-none desktop-hover:tw-w-auto desktop-hover:tw-translate-y-1 desktop-hover:tw-gap-1.5 desktop-hover:tw-border-iron-700/80 desktop-hover:tw-bg-iron-950/90 desktop-hover:tw-px-2.5 desktop-hover:tw-text-xs desktop-hover:tw-font-medium desktop-hover:tw-text-iron-200 desktop-hover:tw-opacity-0 desktop-hover:group-hover:tw-pointer-events-auto desktop-hover:group-hover:tw-translate-y-0 desktop-hover:group-hover:tw-opacity-100 desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-white">{isPending ? <><Spinner dimension={12} /><span className="tw-hidden desktop-hover:tw-inline">Removing</span></> : <><XMarkIcon className="tw-size-4 tw-flex-shrink-0 desktop-hover:tw-size-3.5" /><span className="tw-hidden desktop-hover:tw-inline">Remove</span></>}</button>;
    $[15] = canManageActiveCuration;
    $[16] = handleRemove;
    $[17] = isPending;
    $[18] = shouldUseDetachedRemoveButton;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp3() {}
function _temp2() {}
function _temp() {}
export default function MyStreamWaveCurationContent(t0) {
  const $ = _c(38);
  const {
    wave,
    curationId,
    curationName,
    onDropClick
  } = t0;
  const {
    leaderboardViewStyle
  } = useLayout();
  let t1;
  if ($[0] !== curationId || $[1] !== wave.id) {
    t1 = {
      waveId: wave.id,
      curationId
    };
    $[0] = curationId;
    $[1] = wave.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useWaveDrops(t1);
  const permissionProbeDropId = drops[0]?.id ?? "";
  const t2 = Boolean(permissionProbeDropId);
  let t3;
  if ($[3] !== permissionProbeDropId || $[4] !== t2) {
    t3 = {
      dropId: permissionProbeDropId,
      enabled: t2
    };
    $[3] = permissionProbeDropId;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: t4
  } = useDropCurations(t3);
  let t5;
  if ($[6] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const permissionProbeCurations = t5;
  const isInitialLoading = isFetching && drops.length === 0;
  let t6;
  if ($[8] !== fetchNextPage || $[9] !== hasNextPage || $[10] !== isFetchingNextPage) {
    t6 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage();
    };
    $[8] = fetchNextPage;
    $[9] = hasNextPage;
    $[10] = isFetchingNextPage;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleBottomIntersection = t6;
  let t7;
  if ($[12] !== curationName) {
    t7 = curationName?.trim() ?? "Curation";
    $[12] = curationName;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const curationTitle = t7;
  let t8;
  if ($[14] !== curationId || $[15] !== permissionProbeCurations) {
    t8 = permissionProbeCurations.find(curation => curation.id === curationId)?.authenticated_user_can_curate ?? false;
    $[14] = curationId;
    $[15] = permissionProbeCurations;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const canManageActiveCuration = t8;
  let t9;
  if ($[17] !== canManageActiveCuration || $[18] !== curationId || $[19] !== drops || $[20] !== onDropClick) {
    t9 = drops.map((drop, index) => <MyStreamWaveCurationDropItem key={drop.stableKey} drop={drop} previousDrop={index > 0 ? drops[index - 1] ?? null : null} nextDrop={drops[index + 1] ?? null} curationId={curationId} canManageActiveCuration={canManageActiveCuration} onDropClick={onDropClick} />);
    $[17] = canManageActiveCuration;
    $[18] = curationId;
    $[19] = drops;
    $[20] = onDropClick;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const renderedDrops = t9;
  let content;
  if (isInitialLoading) {
    let t10;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center"><CircleLoader size={CircleLoaderSize.XXLARGE} /></div>;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    content = t10;
  } else {
    if (drops.length === 0) {
      let t10;
      if ($[23] !== curationTitle) {
        t10 = <p className="tw-mb-2 tw-text-base tw-font-semibold tw-text-iron-100">{curationTitle} is empty</p>;
        $[23] = curationTitle;
        $[24] = t10;
      } else {
        t10 = $[24];
      }
      let t11;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = <p className="tw-mb-0 tw-text-sm tw-text-iron-400">This tab will show the drops added to this curation.</p>;
        $[25] = t11;
      } else {
        t11 = $[25];
      }
      let t12;
      if ($[26] !== t10) {
        t12 = <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-px-6"><div className="tw-max-w-md tw-rounded-2xl tw-border tw-border-dashed tw-border-iron-700 tw-bg-iron-950/70 tw-px-6 tw-py-8 tw-text-center">{t10}{t11}</div></div>;
        $[26] = t10;
        $[27] = t12;
      } else {
        t12 = $[27];
      }
      content = t12;
    } else {
      let t10;
      if ($[28] !== handleBottomIntersection || $[29] !== hasNextPage || $[30] !== isFetchingNextPage) {
        t10 = (hasNextPage || isFetchingNextPage) && <div className="tw-py-4">{isFetchingNextPage ? <div className="tw-flex tw-justify-center"><CircleLoader size={CircleLoaderSize.MEDIUM} /></div> : <CommonIntersectionElement onIntersection={handleBottomIntersection} />}</div>;
        $[28] = handleBottomIntersection;
        $[29] = hasNextPage;
        $[30] = isFetchingNextPage;
        $[31] = t10;
      } else {
        t10 = $[31];
      }
      let t11;
      if ($[32] !== renderedDrops || $[33] !== t10) {
        t11 = <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-py-4 md:tw-px-4">{renderedDrops}{t10}</div>;
        $[32] = renderedDrops;
        $[33] = t10;
        $[34] = t11;
      } else {
        t11 = $[34];
      }
      content = t11;
    }
  }
  let t10;
  if ($[35] !== content || $[36] !== leaderboardViewStyle) {
    t10 = <div className="tw-flex tw-h-full tw-min-h-0 tw-w-full tw-min-w-0 tw-flex-grow tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300" style={leaderboardViewStyle}>{content}</div>;
    $[35] = content;
    $[36] = leaderboardViewStyle;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  return t10;
}